/**
 * Startups service module.
 *
 * This module provides data access functions for startup-related operations in the admin panel.
 * It integrates with the backend API to fetch startup data and handles authentication,
 * error handling, and data transformation. The service is designed to be the single
 * source of truth for startup data operations.
 *
 * API Endpoints:
 * - GET /api/v1/admin/startups - Filtered, paginated startups (admin list)
 * - GET /api/v1/admin/startups/export - XLSX export with the same filters
 */

import type { AdminStartupsPage, StartupFilters } from '../types/startup';

export interface GetStartupsParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface GetAdminStartupsParams extends GetStartupsParams, StartupFilters {
  orderBy?: 'name' | 'createdAt';
  sortDir?: 'asc' | 'desc';
}

/** Params for the export endpoint — filters + optional search, no pagination or sort. */
export type ExportStartupsParams = StartupFilters & { search?: string };
import { apiRequest, fetchWithAuth, type ApiError } from '@startus/lib';

// API configuration - uses env variable
const API_BASE_URL = import.meta.env.VITE_API_URL;

if (!API_BASE_URL) {
  throw new Error('VITE_API_URL is not defined');
}

/**
 * Startups service object containing all startup-related API operations.
 */
export const startupsService = {
  /**
   * Fetches filtered, paginated startups from the admin endpoint.
   * Returns enriched profile data (stage, industry, country, etc.).
   */
  async getAdminStartups(params: GetAdminStartupsParams = {}): Promise<AdminStartupsPage> {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    if (params.search) qs.set('search', params.search);
    if (params.stage) qs.set('stage', params.stage);
    if (params.industry?.length) qs.set('industry', params.industry.join(','));
    if (params.country) qs.set('country', params.country);
    if (params.businessModel) qs.set('businessModel', params.businessModel);
    if (params.revenueRange) qs.set('revenueRange', params.revenueRange);
    if (params.growthRate) qs.set('growthRate', params.growthRate);
    if (params.profitable) qs.set('profitable', params.profitable);
    if (params.raisingCapital) qs.set('raisingCapital', params.raisingCapital);
    if (params.priorInvestment) qs.set('priorInvestment', params.priorInvestment);
    if (params.fullTimeStatus) qs.set('fullTimeStatus', params.fullTimeStatus);
    if (params.founderCount) qs.set('founderCount', params.founderCount);
    if (params.orderBy) qs.set('orderBy', params.orderBy);
    if (params.sortDir) qs.set('sortDir', params.sortDir);
    const query = qs.toString() ? `?${qs}` : '';
    return apiRequest<AdminStartupsPage>(`/api/v1/admin/startups${query}`);
  },

  /**
   * Calls GET /api/v1/admin/startups/export with the current filters and returns the raw
   * XLSX blob. The caller is responsible for triggering the browser download.
   * Uses fetchWithAuth directly — apiRequest always parses JSON and would break on binary data.
   */
  async exportStartups(params: ExportStartupsParams = {}): Promise<Blob> {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.stage) qs.set('stage', params.stage);
    if (params.industry?.length) qs.set('industry', params.industry.join(','));
    if (params.country) qs.set('country', params.country);
    if (params.businessModel) qs.set('businessModel', params.businessModel);
    if (params.revenueRange) qs.set('revenueRange', params.revenueRange);
    if (params.growthRate) qs.set('growthRate', params.growthRate);
    if (params.profitable) qs.set('profitable', params.profitable);
    if (params.raisingCapital) qs.set('raisingCapital', params.raisingCapital);
    if (params.priorInvestment) qs.set('priorInvestment', params.priorInvestment);
    if (params.fullTimeStatus) qs.set('fullTimeStatus', params.fullTimeStatus);
    if (params.founderCount) qs.set('founderCount', params.founderCount);
    const query = qs.toString() ? `?${qs}` : '';
    const response = await fetchWithAuth(`${API_BASE_URL}/api/v1/admin/startups/export${query}`);
    if (!response.ok) {
      throw { status: response.status, message: response.statusText } satisfies ApiError;
    }
    return response.blob();
  },
};
